import WithLabel from 'components/atoms/WithLabel'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import { userService } from '../../../core/services'
import * as constants from '../../../static/constants'
import { IUser } from '../../../static/interfaces'
import { Button, Buttons, InputText, InputTextArea, ListItem, Panel } from '../../atoms'
import { Form } from '../../molecules'

interface IValues {
  name: string
  email: string
  subject: string
  body: string
}

interface IProps {
  user: IUser
}

const FIELDS = {
  name: 'name',
  email: 'email',
  subject: 'subject',
  body: 'body',
}

const useContact = (userId: number) => {
  const [confirmationValues, setConfirmationValues] = React.useState(null)
  const [completed, setCompleted] = React.useState(false)

  const handleSubmit = React.useCallback(
    async (initialValues, submitValues) => {
      if (!confirmationValues) {
        setConfirmationValues(submitValues)
      } else {
        const params = {
          ...submitValues,
          user_id: userId,
        }
        await userService.createContact(params)
        setCompleted(true)
      }
    },
    [confirmationValues]
  )
  const handleOnPrev = () => setConfirmationValues(null)

  return {
    confirmationValues,
    handleOnPrev,
    handleSubmit,
    completed,
  }
}

const Contacts: React.FC<IProps> = props => {
  const { confirmationValues, handleOnPrev, handleSubmit, completed } = useContact(props.user.id)

  return (
    <ContactsWrapper>
      <Panel title={I18n.t('generic.contact')} noDecoration={true}>
        {completed ? (
          <p className="Contacts_Completed">{I18n.t('contact.completed')}</p>
        ) : (
          <Form fields={FIELDS} handleSubmit={handleSubmit}>
            <ContactsFormBody presence={confirmationValues ? false : true} />
            <ContactsConfirmation
              presence={confirmationValues ? true : false}
              values={confirmationValues}
              handleOnPrev={handleOnPrev}
            />
          </Form>
        )}
      </Panel>
    </ContactsWrapper>
  )
}

const ContactsFormBody: React.FC<{ presence: boolean }> = props => (
  <div style={{ display: props.presence ? 'block' : 'none' }}>
    <ListItem>
      <InputText name="name" label={I18n.t('generic.name')} defaultValue="" required={true} />
    </ListItem>
    <ListItem>
      <InputText name="email" label={I18n.t('generic.email')} defaultValue="" required={true} />
    </ListItem>
    <ListItem>
      <InputText name="subject" label={I18n.t('contact.subject')} defaultValue="" required={true} />
    </ListItem>
    <ListItem>
      <InputTextArea name="body" label={I18n.t('contact.body')} defaultValue="" required={true} />
    </ListItem>
    <Buttons buttonSize={100}>
      <Button primary={true}>{I18n.t('generic.next')}</Button>
    </Buttons>
  </div>
)

interface IContactsConfirmationProps {
  values: null | IValues
  presence: boolean
  handleOnPrev(): void
}

const ContactsConfirmation: React.FC<IContactsConfirmationProps> = props => (
  <div style={{ display: props.presence ? 'block' : 'none' }}>
    <p className="Contacts_Confirmation">{I18n.t('contact.confirmation')}</p>
    {props.values && (
      <div>
        <ListItem>
          <WithLabel label={I18n.t('generic.name')}>{props.values.name}</WithLabel>
        </ListItem>
        <ListItem>
          <WithLabel label={I18n.t('generic.email')}>{props.values.email}</WithLabel>
        </ListItem>
        <ListItem>
          <WithLabel label={I18n.t('contact.subject')}>{props.values.subject}</WithLabel>
        </ListItem>
        <ListItem>
          <WithLabel label={I18n.t('contact.body')}>{props.values.body}</WithLabel>
        </ListItem>
      </div>
    )}
    <Buttons buttonSize={100}>
      <Button handleClick={props.handleOnPrev}>{I18n.t('generic.back')}</Button>
      <Button primary={true}>{I18n.t('generic.submit')}</Button>
    </Buttons>
  </div>
)

const ContactsWrapper = styled.div`
  background-color: ${constants.COLORS.white};
  height: calc(100vh - ${constants.HEADER_HEIGHT}px);
  padding: 24px 12px;
  overflow: auto;

  .Contacts_Completed,
  .Contacts_Confirmation {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 20px;

    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 14px;
    }
  }
`

export default Contacts
